import React, { useEffect, useMemo, useState } from "react";
import CollapsibleActionSection from "./CollapsibleActionSection";

const normalizeTechAssign = (value, keepBlank = false) => {
  if (Array.isArray(value)) {
    return keepBlank ? value : value.map((item) => String(item || "").trim()).filter(Boolean);
  }
  if (typeof value === "string" && value.trim()) return [value.trim()];
  return [];
};

const statusOptions = ["Active", "Completed", "Invoice Sent", "On Hold"];

const buttonStyle = {
  padding: "9px 12px",
  borderRadius: 8,
  border: "1px solid #1d4ed8",
  background: "#dbeafe",
  color: "#0f172a",
  fontWeight: 800,
  cursor: "pointer",
};

const QuickJobFieldEditor = ({ job, usersList = [], onSave, disabled = false }) => {
  const [mode, setMode] = useState("");
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({});

  const techOptions = useMemo(
    () =>
      usersList
        .map((user) => ({
          id: user.id,
          name: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
          role: user.role || "",
        }))
        .filter((user) => user.name)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [usersList]
  );

  useEffect(() => {
    setMode("");
    setDraft({});
  }, [job?.id, job?.occurrenceDate, job?.startDate]);

  const openEditor = (nextMode) => {
    if (!job) return;
    setMode(nextMode);
    setDraft({
      startDate: job.startDate || "",
      endDate: job.endDate || job.startDate || "",
      techAssign: normalizeTechAssign(job.techAssign),
      scope: job.scope || "",
      jobDescription: job.jobDescription || "",
      status: job.status || "Active",
    });
  };

  const save = async () => {
    if (!job || typeof onSave !== "function") return;

    const changes = {};
    if (mode === "dates") {
      if (!draft.startDate) {
        window.alert("Please select a start date.");
        return;
      }
      changes.startDate = draft.startDate;
      changes.endDate =
        !draft.endDate || draft.startDate > draft.endDate
          ? draft.startDate
          : draft.endDate;
    } else if (mode === "techs") {
      changes.techAssign = normalizeTechAssign(draft.techAssign);
    } else if (mode === "details") {
      changes.scope = draft.scope || "";
      changes.jobDescription = draft.jobDescription || "";
    } else if (mode === "status") {
      if (!draft.status) {
        window.alert("Please select a status.");
        return;
      }
      changes.status = draft.status;
    } else {
      return;
    }

    setSaving(true);
    try {
      const saved = await onSave(changes);
      if (saved === false) return;
      setMode("");
      setDraft({});
    } catch (error) {
      console.error("Quick job field update failed:", error);
      window.alert("Could not save this job change. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const addTech = () =>
    setDraft((prev) => ({
      ...prev,
      techAssign: [...normalizeTechAssign(prev.techAssign, true), ""],
    }));

  const updateTech = (index, value) =>
    setDraft((prev) => {
      const next = [...normalizeTechAssign(prev.techAssign, true)];
      next[index] = value;
      return { ...prev, techAssign: next };
    });

  const removeTech = (index) =>
    setDraft((prev) => ({
      ...prev,
      techAssign: normalizeTechAssign(prev.techAssign, true).filter((_, i) => i !== index),
    }));

  return (
    <>
      <CollapsibleActionSection title="Edit Job" tone="edit" defaultOpen={false}>
        <button type="button" style={buttonStyle} disabled={disabled} onClick={() => openEditor("dates")}>
          Change Dates
        </button>
        <button type="button" style={buttonStyle} disabled={disabled} onClick={() => openEditor("techs")}>
          Add / Remove Techs
        </button>
        <button type="button" style={buttonStyle} disabled={disabled} onClick={() => openEditor("details")}>
          Edit Scope & Job Details
        </button>
        <button type="button" style={buttonStyle} disabled={disabled} onClick={() => openEditor("status")}>
          Change Status
        </button>
      </CollapsibleActionSection>

      {mode && (
        <>
          <div
            onClick={() => !saving && setMode("")}
            style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 10020 }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "min(560px, 92vw)",
              maxHeight: "85vh",
              overflowY: "auto",
              background: "#fff",
              borderRadius: 12,
              padding: 20,
              boxShadow: "0 24px 60px rgba(0,0,0,.3)",
              zIndex: 10021,
            }}
          >
            <h3 style={{ marginTop: 0 }}>
              {mode === "dates"
                ? "Change Job Dates"
                : mode === "techs"
                ? "Add / Remove Technicians"
                : mode === "status"
                ? "Change Job Status"
                : "Edit Scope & Job Details"}
            </h3>

            {mode === "dates" && (
              <div style={{ display: "grid", gap: 12 }}>
                <label style={{ fontWeight: 800 }}>
                  Start Date
                  <input
                    type="date"
                    value={draft.startDate || ""}
                    onChange={(event) => {
                      const startDate = event.target.value;
                      setDraft((prev) => ({
                        ...prev,
                        startDate,
                        endDate: !prev.endDate || startDate > prev.endDate ? startDate : prev.endDate,
                      }));
                    }}
                    style={{ width: "100%", marginTop: 6 }}
                  />
                </label>
                <label style={{ fontWeight: 800 }}>
                  End Date
                  <input
                    type="date"
                    min={draft.startDate || undefined}
                    value={draft.endDate || ""}
                    onChange={(event) => setDraft((prev) => ({ ...prev, endDate: event.target.value }))}
                    style={{ width: "100%", marginTop: 6 }}
                  />
                </label>
                <div style={{ fontSize: 13, color: "#475569" }}>
                  When the new start date is after the current end date, the end date automatically changes to match the new start date.
                </div>
              </div>
            )}

            {mode === "techs" && (
              <div>
                {normalizeTechAssign(draft.techAssign, true).map((tech, index) => (
                  <div key={index} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <select value={tech} onChange={(event) => updateTech(index, event.target.value)} style={{ flex: 1 }}>
                      <option value="">Select Technician</option>
                      {techOptions.map((option) => (
                        <option key={option.id} value={option.name}>
                          {option.name}{option.role ? ` (${option.role})` : ""}
                        </option>
                      ))}
                    </select>
                    <button type="button" onClick={() => removeTech(index)} style={{ ...buttonStyle, borderColor: "#b91c1c", background: "#fee2e2" }}>
                      Remove
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addTech} style={buttonStyle}>Add Technician</button>
              </div>
            )}

            {mode === "status" && (
              <div style={{ display: "grid", gap: 12 }}>
                <label style={{ fontWeight: 800 }}>
                  Status
                  <select
                    value={draft.status || "Active"}
                    onChange={(event) =>
                      setDraft((prev) => ({ ...prev, status: event.target.value }))
                    }
                    style={{ width: "100%", marginTop: 6 }}
                  >
                    {statusOptions.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}

            {mode === "details" && (
              <div style={{ display: "grid", gap: 12 }}>
                <label style={{ fontWeight: 800 }}>
                  Scope
                  <input
                    type="text"
                    value={draft.scope || ""}
                    onChange={(event) => setDraft((prev) => ({ ...prev, scope: event.target.value }))}
                    style={{ width: "100%", marginTop: 6 }}
                  />
                </label>
                <label style={{ fontWeight: 800 }}>
                  Job Details / Description
                  <textarea
                    rows={8}
                    value={draft.jobDescription || ""}
                    onChange={(event) => setDraft((prev) => ({ ...prev, jobDescription: event.target.value }))}
                    style={{ width: "100%", marginTop: 6, resize: "vertical" }}
                  />
                </label>
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
              <button type="button" disabled={saving} onClick={() => setMode("")} style={{ ...buttonStyle, borderColor: "#b91c1c", background: "#fee2e2" }}>
                Cancel
              </button>
              <button type="button" disabled={saving} onClick={save} style={{ ...buttonStyle, background: "#1d4ed8", color: "#fff" }}>
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default QuickJobFieldEditor;
